import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { useCallback, useRef } from "react";
import type { PaneSide } from "./types";

interface SplitterProps {
  ratio: number;
  fullscreenPane: PaneSide | null;
  focusedPane: PaneSide;
  onRatioChange: (ratio: number) => void;
  left: ReactNode;
  right: ReactNode;
}

const MIN_RATIO = 0.25;
const MAX_RATIO = 0.75;

function clampRatio(value: number): number {
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, value));
}

function ratioTrack(value: number): string {
  return `${Number(value.toFixed(4))}fr`;
}

export function Splitter({
  ratio,
  fullscreenPane,
  focusedPane,
  onRatioChange,
  left,
  right,
}: SplitterProps) {
  const splitterRef = useRef<HTMLDivElement>(null);
  const clampedRatio = clampRatio(ratio);
  const leftRatio = ratioTrack(clampedRatio);
  const rightRatio = ratioTrack(1 - clampedRatio);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const splitter = splitterRef.current;
      if (!splitter || fullscreenPane) return;
      const splitterElement = splitter;
      event.currentTarget.setPointerCapture(event.pointerId);

      function onPointerMove(moveEvent: PointerEvent) {
        const rect = splitterElement.getBoundingClientRect();
        if (rect.width <= 0) return;
        onRatioChange(clampRatio((moveEvent.clientX - rect.left) / rect.width));
      }

      function onPointerUp() {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      }

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [fullscreenPane, onRatioChange],
  );

  const style = {
    "--left-ratio": leftRatio,
    "--right-ratio": rightRatio,
  } as CSSProperties;
  const fullscreenClass = fullscreenPane ? ` fullscreen-${fullscreenPane}` : "";

  return (
    <main
      className={`trading-splitter${fullscreenClass}`}
      data-focused-pane={focusedPane}
      data-fullscreen-pane={fullscreenPane ?? undefined}
      ref={splitterRef}
      style={style}
    >
      <div className={fullscreenPane === "right" ? "pane-sidebar" : "pane-slot"}>{left}</div>
      <div
        aria-label="Resize panes"
        className="splitter-handle"
        onDoubleClick={() => onRatioChange(0.5)}
        onPointerDown={onPointerDown}
        role="separator"
        tabIndex={0}
      />
      <div className={fullscreenPane === "left" ? "pane-sidebar" : "pane-slot"}>{right}</div>
    </main>
  );
}
